// src/components/admin/mail/MailMessageRow.tsx
import React from 'react';
import styled from 'styled-components';
import { CornerUpLeft, Paperclip } from 'lucide-react';
import { MailMessageSummary } from '../../../types/mail';
import { formatAddress, formatMailDate } from './mailUtils';

/** A real <button>, so Tab / Enter / Space and focus rings all work without any ARIA. */
const Row = styled.button<{ $selected: boolean; $unread: boolean; $accent: string }>`
    display: grid;
    grid-template-columns: 18px minmax(0, 1fr);
    gap: 0.65rem;
    width: 100%;
    padding: 0.85rem 1.25rem;
    border: none;
    border-bottom: 1px solid #f1f5f9;
    border-left: 3px solid ${(p) => (p.$selected ? p.$accent : 'transparent')};
    background: ${(p) => (p.$selected ? '#f1f5f9' : p.$unread ? '#ffffff' : '#f8fafc')};
    text-align: left;
    cursor: pointer;
    transition: background 0.15s ease;
    box-sizing: border-box;

    &:hover {
        background: ${(p) => (p.$selected ? '#e2e8f0' : '#f1f5f9')};
    }

    &:focus-visible {
        outline: 3px solid ${(p) => p.$accent}55;
        outline-offset: -3px;
    }
`;

/** Fixed-width gutter so the eye can scan straight down the column of unread dots. */
const Gutter = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding-top: 4px;
`;

const UnreadDot = styled.span<{ $accent: string }>`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${(p) => p.$accent};
    display: block;
`;

const Body = styled.div`
    min-width: 0;
`;

const TopLine = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.5rem;
`;

const Sender = styled.span<{ $unread: boolean }>`
    font-size: 0.88rem;
    font-weight: ${(p) => (p.$unread ? 700 : 500)};
    color: ${(p) => (p.$unread ? '#1e293b' : '#475569')};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const DateText = styled.span`
    font-size: 0.72rem;
    color: #94a3b8;
    flex-shrink: 0;
`;

const Subject = styled.div<{ $unread: boolean }>`
    display: flex;
    align-items: center;
    gap: 0.4rem;
    margin-top: 0.2rem;
    font-size: 0.85rem;
    font-weight: ${(p) => (p.$unread ? 700 : 400)};
    color: ${(p) => (p.$unread ? '#334155' : '#64748b')};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const Icons = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    color: #94a3b8;
    flex-shrink: 0;
`;

interface Props {
    message: MailMessageSummary;
    selected: boolean;
    accent: string;
    onOpen: (uid: number) => void;
}

const MailMessageRow: React.FC<Props> = ({ message, selected, accent, onOpen }) => {
    const unread = !message.seen;
    const sender = formatAddress(message.from) || '(neznámy odosielateľ)';

    return (
        <Row
            type="button"
            $selected={selected}
            $unread={unread}
            $accent={accent}
            onClick={() => onOpen(message.uid)}
            aria-label={`${unread ? 'Neprečítané. ' : ''}${sender}: ${message.subject}`}
        >
            <Gutter>{unread && <UnreadDot $accent={accent} aria-hidden="true" />}</Gutter>
            <Body>
                <TopLine>
                    <Sender $unread={unread}>{sender}</Sender>
                    <DateText>{formatMailDate(message.sentAt)}</DateText>
                </TopLine>
                <Subject $unread={unread}>
                    <span
                        style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            minWidth: 0,
                        }}
                    >
                        {message.subject}
                    </span>
                    <Icons>
                        {message.answered && <CornerUpLeft size={13} aria-label="Odpovedané" />}
                        {message.hasAttachments && <Paperclip size={13} aria-label="Obsahuje prílohu" />}
                    </Icons>
                </Subject>
            </Body>
        </Row>
    );
};

export default MailMessageRow;
